import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'react-i18next';
import variableTypes from './variable-types';
import {
  VariableCommonContext,
  VariableErrorTypes,
} from 'pages/AutomationEngine/dialogs/VariableWizard/VariableWizardContext';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import {
  ArrayAggregationMode,
  SourceVariableType,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import type {
  VariableOption,
  CommonVariableDefinitionType,
  VariableUpdateType,
} from 'pages/AutomationEngine/types';
import {
  defaultDisabledTypes,
  useVariableMetaData,
} from 'pages/AutomationEngine/dialogs/VariableWizard/useVariableMetaData';
import { useVariableLogic } from 'pages/AutomationEngine/dialogs/VariableWizard/useVariableLogic';
import { useErrors } from './useErrors';
import { ContextErrors, UpdateVariableContextValue } from './types';
import { ArrayAggregationModeOptions } from 'pages/AutomationEngine/dialogs/VariableWizard/constants';

export const defaultContextValue: UpdateVariableContextValue = {
  id: '',
  chosenVariable: undefined,
  type: 'update_variable',
  variable: {
    name: '',
    dataType: AUTOMATION_VARIABLE_DATA_TYPES.string,
    outputFormat: undefined,
    isArray: false,
    isArrayItemVariable: false,
    sourceArrayVariable: undefined,
  },
  sources: [],
  hasError: false,
  isRequired: true,
  arrayAggregationMode: ArrayAggregationModeOptions.ADD_ALL,
  deduplicateArray: false,
};

type TypeLookup = Record<VariableUpdateType, SourceVariableType>;

const typeLookUp: TypeLookup = {
  ['static']: {
    sourceType: 'static',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    staticVariable: {
      id: '',
      value: '',
      valueHtml: '',
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
    },
    id: '',
  },
  ['field_value']: {
    sourceType: 'field_value',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    fieldVariable: {
      id: '',
      value: '',
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
      sourceSubtype: undefined,
      field: undefined,
      relationshipField: undefined,
    },
    id: '',
  },
  ['variable']: {
    sourceType: 'variable',
    sourceSubtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
    automationVariable: {
      id: '',
      value: '',
      variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
      valueCategory: undefined,
    },
    id: '',
  },
};

export const isVariableUpdateType = (
  type: CommonVariableDefinitionType
): type is VariableUpdateType => Object.hasOwn(typeLookUp, type);

const getTypeLookUp = (type: CommonVariableDefinitionType) =>
  isVariableUpdateType(type) ? typeLookUp[type] : undefined;

export const labelLookUp = (
  t: TFunction
): Record<VariableUpdateType, string> => ({
  ['static']: t('Static (Default)'),
  ['field_value']: t('Field Value'),
  ['variable']: t('Agentic Workflow Variable'),
});

type VariableUpdateContext = VariableCommonContext & {
  getSourceLabel: (type: CommonVariableDefinitionType) => string;
  updateValues: any;
  setUpdateValues: () => void;
  setUpdateVariable: (variable: any) => void;
  currentErrors: ContextErrors;
};

const context = createContext<VariableUpdateContext>({
  id: '',
  variable: defaultContextValue.variable,
  arrayAggregationMode: defaultContextValue.arrayAggregationMode,
  deduplicateArray: defaultContextValue.deduplicateArray,
  nameError: null,
  setNameError: () => {},
  setVariableName: () => {},
  setVariableDataType: () => {},
  setVariableDataSubtype: () => {},
  setVariableOutputFormat: () => {},
  setVariableIsArray: () => {},
  setArrayAggregationMode: () => {},
  setDeduplicateArray: () => {},
  values: {},
  setValues: () => {},
  isValid: false,
  checkActive: () => false,
  sources: [],
  sourcesMask: [],
  disabledTypes: defaultDisabledTypes,
  addVariableSource: () => {},
  moveVariableSource: () => {},
  deleteVariableSource: () => {},
  staticIndex: -1,
  updateVariableSource: () => {},
  errors: {},
  setErrors: () => {},
  clickValidate: (): { isValid: boolean } => ({ isValid: false }),
  getSourceLabel: () => '',
  uniqueNames: [],
  businessObjectOptions: [],
  activityLoggedContext: { activities: [] },
  activityPastDueContext: { activities: [] },
  webhookTriggerContext: { webhooks: [] },
  metaData: {
    detailed_log_templates: {},
  },
  checkVariableFromFieldCompatibility: () => false,
  checkVariableVariableCompatibility: () => false,
  getVariableVariableCompatibility: () => ({}),
  showWarning: false,
  onHideWarningConfirmed: () => {},
  onHideWarningCanceled: () => {},
  validateDataSubtype: null,
  showOutputFormat: false,
  outputFormatOptions: undefined,
  // additional for VariableUpdateContext
  updateValues: {},
  setUpdateValues: () => {},
  setUpdateVariable: () => {},
  currentErrors: { chosenVariable: { hasError: false, message: undefined } },
});

export const useVariableUpdateContext = () => useContext(context);

type VariableUpdateProviderProps = {
  valuesProp: UpdateVariableContextValue;
  onChange: Dispatch<SetStateAction<UpdateVariableContextValue>>;
};

export const VariableUpdateProviderInner = ({
  children,
  valuesProp,
  onChange,
}: PropsWithChildren<VariableUpdateProviderProps>) => {
  const { t } = useTranslation();

  const translatedLabels = useMemo(() => labelLookUp(t), [t]);

  const [validateOnLoad, setValidateOnLoad] = useState(false);

  const { sources, variable, arrayAggregationMode, deduplicateArray } = {
    ...defaultContextValue,
    ...valuesProp,
  };

  const [errors, setErrors] = useState<VariableErrorTypes>({});

  const staticIndex = useMemo(() => {
    return (sources || []).findIndex(
      ({ sourceType }) => sourceType === 'static'
    );
  }, [sources]);

  const setVariableSources = useCallback(
    (sources: SourceVariableType[]) =>
      onChange((prev) => ({ ...prev, sources })),
    [onChange]
  );

  const {
    addVariableSource,
    moveVariableSource,
    deleteVariableSource,
    updateVariableSource,
  } = useVariableLogic(
    variable,
    staticIndex,
    sources,
    setVariableSources,
    false,
    errors,
    setErrors,
    getTypeLookUp
  );

  const setUpdateVariable = useCallback(
    (chosenVariable: VariableOption) => {
      onChange((prev) => ({
        ...prev,
        chosenVariable,
        sources:
          // if new variable is compatible with previous, keep sources, otherwise reset
          chosenVariable.meta?.dataType === prev.variable?.dataType &&
          chosenVariable.meta?.isArray === prev.variable?.isArray &&
          chosenVariable.meta?.dataSubtype?.value ===
            prev.variable?.dataSubtype?.value
            ? prev.sources
            : [],
        variable: {
          ...prev.variable,
          name: chosenVariable.label,
          dataType: chosenVariable?.meta?.dataType,
          dataSubtype: chosenVariable?.meta?.dataSubtype,
          isArray: chosenVariable?.meta?.isArray ?? false,
          meta: chosenVariable?.meta,
        },
        isRequired:
          prev.chosenVariable?.meta?.isArray !== chosenVariable?.meta?.isArray
            ? !chosenVariable?.meta?.isArray
            : prev.isRequired,
      }));
    },
    [onChange]
  );

  const {
    metaData,
    checkVariableFromFieldCompatibility,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
  } = useVariableMetaData(typeLookUp, false);

  const getSourceLabel = useCallback(
    (type: CommonVariableDefinitionType) => {
      return isVariableUpdateType(type) ? translatedLabels[type] : t('Unknown');
    },
    [translatedLabels, t]
  );

  useEffect(() => {
    if (sources && !validateOnLoad) {
      const newErrors = sources.reduce((acc, variableSource) => {
        let errs = {};
        const validate =
          variableTypes?.[variableSource.sourceType as VariableUpdateType]
            ?.validate;
        if (validate) {
          const { errors } = validate({
            values: variableSource,
            checkRequired: false,
            t,
          });

          errs = errors.reduce((currentErrors: any, err: any) => {
            return { ...currentErrors, ...err };
          }, {});
        }
        if (Object.keys(errs).length) {
          return { ...acc, [variableSource.id]: { ...errs } };
        }
        return acc;
      }, {});

      setErrors(newErrors);

      setValidateOnLoad(true);
    }
  }, [sources, t, validateOnLoad]);

  const currentErrors = useErrors(valuesProp, onChange, null);

  useEffect(() => {
    const hasError =
      currentErrors?.chosenVariable?.hasError ||
      Object.keys(errors)?.length > 0;

    onChange((ps: UpdateVariableContextValue) => ({ ...ps, hasError }));
  }, [currentErrors?.chosenVariable?.hasError, errors, onChange]);

  const setArrayAggregationMode = useCallback(
    (arrayAggregationMode: ArrayAggregationMode) => {
      onChange((prev: UpdateVariableContextValue) => {
        return {
          ...prev,
          arrayAggregationMode,
          deduplicateArray: false,
        };
      });
    },
    [onChange]
  );

  const setDeduplicateArray = useCallback(
    (deduplicateArray: boolean) => {
      onChange((prev: UpdateVariableContextValue) => {
        return {
          ...prev,
          deduplicateArray,
        };
      });
    },
    [onChange]
  );

  const ctx = {
    id: '',
    variable,
    arrayAggregationMode,
    deduplicateArray,
    nameError: null,
    setNameError: () => {},
    setVariableName: () => {},
    setVariableDataType: () => {},
    setVariableDataSubtype: () => {},
    setVariableOutputFormat: () => {},
    setVariableIsArray: () => {},
    setArrayAggregationMode,
    setDeduplicateArray,
    values: {},
    setValues: () => {},
    isValid: false,
    checkActive: () => false,
    sources,
    sourcesMask: [],
    disabledTypes: defaultDisabledTypes,
    addVariableSource,
    moveVariableSource,
    deleteVariableSource,
    staticIndex,
    updateVariableSource,
    errors,
    setErrors,
    clickValidate: (): { isValid: boolean } => ({ isValid: false }),
    getSourceLabel,
    uniqueNames: [],
    businessObjectOptions: [],
    activityLoggedContext: { activities: [] },
    activityPastDueContext: { activities: [] },
    webhookTriggerContext: { webhooks: [] },
    metaData,
    checkVariableFromFieldCompatibility,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    showWarning: false,
    onHideWarningConfirmed: () => {},
    onHideWarningCanceled: () => {},
    validateDataSubtype: null,
    showOutputFormat: false,
    outputFormatOptions: [],
    // update specific

    updateValues: valuesProp,
    setUpdateValues: () => {},
    setUpdateVariable,
    currentErrors,
  };

  return <context.Provider value={ctx}> {children} </context.Provider>;
};

// VariableUpdateProvider has side effects calling onChange and making state management vague
// so it should not be put into ActionWizardCore but rather used within the specific context it is needed.
// We might need to refactor this
export const VariableUpdateProvider = (
  props: PropsWithChildren<VariableUpdateProviderProps>
) => {
  return props.valuesProp.type === 'update_variable' ? (
    <VariableUpdateProviderInner {...props} />
  ) : (
    props.children
  );
};
